import { useFlag } from '@calendly/feature-check-react';
import { I18n } from '@calendly/i18n';
import { BareButton } from '@calendly/ui/components/bare-button';
import { ComponentGroup } from '@calendly/ui/components/component-group';
import { ArrowChevronRightIcon, ArrowExpandIcon, CloseIcon } from '@calendly/ui/components/icons';
import { Link } from '@calendly/ui/components/link';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { Modal } from '@calendly/ui/components/modal';
import { Sheet } from '@calendly/ui/components/sheet';
import { BodyText, HeadingText } from '@calendly/ui/components/typography';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { useContext, useEffect, useState, forwardRef } from 'react';
import { useGetSendOptionsQuery } from 'common';
import { ShareContext } from 'features/tracked_meetings/share/context';
import { PasscodeCheckbox } from 'features/tracked_meetings/share/passcode_checkbox';
import { ShareEmailModal } from 'features/tracked_meetings/share/send_email/email_modal';
import { ModalCopyButton } from 'features/tracked_meetings/share/share_link/modal_copy_button';
import { TemplateSummaryContext } from 'features/tracked_meetings/template_summary_context';
import SendEmailAnyone from 'images/tracked_meetings/send-email-anyone.svg';
import SendEmailAttendees from 'images/tracked_meetings/send-email-attendees.svg';
import SendEmail from 'images/tracked_meetings/send-email.svg';
import ShareRecap from 'images/tracked_meetings/share-recap.svg';
import { useModalState } from './hooks/use_modal_state';
const shareContainerCss = "sfal0vc";
const ShareContainer = forwardRef((props, ref) => <Sheet ref={ref} {...props} className={classNames(shareContainerCss, props.className)} />);
const shareModalHeaderIsPhoneCss = "s1h8chnn";
const shareModalHeaderCss = "s1h88etb";
const ShareModalHeader = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(shareModalHeaderCss, {
    [shareModalHeaderIsPhoneCss]: isPhone
  }, props.className)} />;
});
const alignmentContainerCss = "a1501vl7";
const AlignmentContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(alignmentContainerCss, props.className)} />;
});
const sendContainerWrapperCss = "s11qhjc1";
const SendContainerWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(sendContainerWrapperCss, props.className)} />;
});
const modalContentWrapperCss = "mepjm8m";
const ModalContentWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(modalContentWrapperCss, props.className)} />;
});
const shareModalContentCss = "sb1d8n5";
const ShareModalContent = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(shareModalContentCss, props.className)} />;
});
const contentContainerCss = "c1n38z53";
const ContentContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(contentContainerCss, props.className)} />;
});
const headerCss = "hb8obu";
const Header = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(headerCss, props.className)} />;
});
const textContainerCss = "t1jaue7g";
const TextContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(textContainerCss, props.className)} />;
});
const linkContainerCss = "l18kio9k";
const LinkContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(linkContainerCss, props.className)} />;
});
const urlTextCss = "u1fl8jzv";
const UrlText = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'p';
  return <Component ref={ref} {...restProps} className={classNames(urlTextCss, props.className)} />;
});
const styledBareButtonCss = "ss48xgd";
const StyledBareButton = forwardRef((props, ref) => <BareButton ref={ref} {...props} className={classNames(styledBareButtonCss, props.className)} />);
const sendContainerCss = "s1sur28p";
const SendContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(sendContainerCss, props.className)} />;
});
const defaultCalendlyEmail = {
  emailAddress: 'notifications@calendly.com',
  accessGranted: true,
  type: 'calendly'
};
export const ShareModal = ({
  isSnippet,
  isOpen,
  onClose,
  onShareLinkClick,
  videoPageUrl,
  attendeesFromSearchData
}) => {
  const {
    togglePasscode
  } = useContext(ShareContext);
  const {
    isSample
  } = useContext(TemplateSummaryContext);
  const {
    data,
    isSuccess
  } = useGetSendOptionsQuery({
    ownerType: 'users',
    ownerId: 'me'
  }, {
    skip: isSample
  });
  const {
    currentModal,
    isConfirmationPopupOpen,
    openModal,
    closeModal,
    handleOpenConfirmationPopup,
    handleCloseConfirmationPopup,
    handleConfirmAction
  } = useModalState();
  const [sendOptions, setSendOptions] = useState([defaultCalendlyEmail]);
  const isMiEnabled = useFlag('meeting_intelligence');
  const [isAttendeeModal, setIsAttendeeModal] = useState(false);
  useEffect(() => {
    if (isSuccess) {
      setSendOptions(currentSendOptions => [...currentSendOptions, ...data.sendOptions]);
    }
  }, [isSuccess, data]);
  let headerText;
  if (isSnippet) {
    headerText = I18n.t('publisher.meeting_intelligence.share.snippet_modal_header');
  } else if (isMiEnabled) {
    headerText = I18n.t('publisher.meeting_intelligence.share.modal_header_la');
  } else {
    headerText = I18n.t('publisher.meeting_intelligence.share.modal_header');
  }
  const copyHeaderText = isSnippet ? I18n.t('publisher.meeting_intelligence.share.snippet_copy_header') : I18n.t('publisher.meeting_intelligence.share.copy_header');
  const copyBodyText = isSnippet ? I18n.t('publisher.meeting_intelligence.share.snippet_copy_body') : I18n.t('publisher.meeting_intelligence.share.copy_body');
  const handleClickAttendeeModal = () => {
    setIsAttendeeModal(true);
    openModal('email');
  };
  const handleClickNonAttendeeModal = () => {
    setIsAttendeeModal(false);
    openModal('email');
  };
  return <Modal isOpen={isOpen} onClose={onClose} data-calendly-label="share-modal">
      {currentModal === 'email' ? <ShareEmailModal onClose={onClose} onBack={() => openModal('share')} isConfirmationPopupOpen={isConfirmationPopupOpen} handleOpenConfirmationPopup={handleOpenConfirmationPopup} handleCloseConfirmationPopup={handleCloseConfirmationPopup} handleConfirmAction={handleConfirmAction} closeModal={closeModal} emails={sendOptions} onShareLinkClick={onShareLinkClick} togglePasscode={togglePasscode} videoPageUrl={videoPageUrl} isAttendeeModal={isAttendeeModal} attendeesFromSearchData={attendeesFromSearchData} /> : <ShareContainer>
          <ShareModalHeader>
            <HeadingText level={2}>
              <AlignmentContainer>
                {!isMiEnabled && <img src={ShareRecap} alt="" />}
                {headerText}
              </AlignmentContainer>
            </HeadingText>
            <StyledBareButton onClick={onClose} aria-label={I18n.t('publisher.action.close')}>
              <CloseIcon size="large" />
            </StyledBareButton>
          </ShareModalHeader>
          <ModalContentWrapper>
            {isMiEnabled && <SendContainerWrapper>
                <SendContainer onClick={handleClickAttendeeModal}>
                  <img src={SendEmailAttendees} alt="" />
                  <TextContainer>
                    <BodyText size="medium" weight="bold">
                      {I18n.t('publisher.meeting_intelligence.share.send_email_attendees_header_la')}
                    </BodyText>
                    <BodyText size="medium" color="muted">
                      {I18n.t('publisher.meeting_intelligence.share.send_email_attendees_body_la')}
                    </BodyText>
                  </TextContainer>
                  <ArrowChevronRightIcon />
                </SendContainer>
                <SendContainer onClick={handleClickNonAttendeeModal}>
                  <img src={SendEmailAnyone} alt="" />
                  <TextContainer>
                    <BodyText size="medium" weight="bold">
                      {I18n.t('publisher.meeting_intelligence.share.send_email_header_la')}
                    </BodyText>
                    <BodyText size="medium" color="muted">
                      {I18n.t('publisher.meeting_intelligence.share.send_email_body_la')}
                    </BodyText>
                  </TextContainer>
                  <ArrowChevronRightIcon />
                </SendContainer>
              </SendContainerWrapper>}
            {!isMiEnabled && <>
                <SendContainer onClick={() => openModal('email')}>
                  <img src={SendEmail} alt="" />
                  <TextContainer>
                    <BodyText size="medium" weight="bold">
                      {I18n.t('publisher.meeting_intelligence.share.send_email_header')}
                    </BodyText>
                    <BodyText size="medium" color="muted">
                      {I18n.t('publisher.meeting_intelligence.share.send_email_body')}
                    </BodyText>
                  </TextContainer>
                  <ArrowChevronRightIcon />
                </SendContainer>
                <ShareModalContent>
                  <ContentContainer>
                    <Header>
                      <BodyText inline size="medium" weight="bold">
                        {copyHeaderText}
                      </BodyText>
                      <Link underline="false" rel="opener" target="_blank" href={videoPageUrl}>
                        <MinimalButton data-calendly-label="preview-video-button" tabIndex="-1" fontSize="small">
                          {I18n.t('publisher.meeting_intelligence.share.preview_button')}
                          <ArrowExpandIcon />
                        </MinimalButton>
                      </Link>
                    </Header>
                    <BodyText size="medium" color="muted">
                      {copyBodyText}
                    </BodyText>
                  </ContentContainer>
                  <ComponentGroup>
                    <LinkContainer>
                      <UrlText size="small">{videoPageUrl}</UrlText>
                      <ModalCopyButton content={videoPageUrl} onShareLinkClick={onShareLinkClick} />
                    </LinkContainer>
                  </ComponentGroup>
                  <PasscodeCheckbox />
                </ShareModalContent>
              </>}
          </ModalContentWrapper>
        </ShareContainer>}
    </Modal>;
};

require("./share_modal.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./share_modal.js");